import { MonetaryValue, MonetaryValueDataObjectType, ValueWithTrend } from 'ComponentLibrary/src/common/money'
import { Card, styled } from '@mui/material'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { ArrowRightIcon } from '@phosphor-icons/react'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { RoundImage } from 'ComponentLibrary/src/atoms/Image'
import AssetPlaceholderImage from 'ComponentLibrary/src/assets/images/asset-placeholder.svg'
import { HighlightedLabeledValue, NumericLabeledValue } from 'ComponentLibrary/src/atoms/LabeledValue'
import { Percentage } from 'ComponentLibrary/src/common/percentage'
import palette from 'ComponentLibrary/src/styles/colors'
import dayjs from 'dayjs'
import { getLocalizedDateString } from 'ComponentLibrary/src/common/date'
import { getTrendColor } from 'ComponentLibrary/src/common/colors'
import { isNotNullOrUndefined, tokens } from 'ComponentLibrary'

export type GeographicPortfolioPopupProps = {
    date?: string
    title: string
    market_value?: ValueWithTrend<MonetaryValueDataObjectType>
    value_per_sqm?: MonetaryValueDataObjectType
    navigateToAsset?: React.MouseEventHandler<HTMLButtonElement>
    image?: string
    imageThumbnail?: string
    locale?: string
    copy?: Partial<GeographicPortfolioPopupCopy>
}

export type GeographicPortfolioPopupCopy = {
    marketValueLabel: string
    valuePerSquareMeterLabel: string
    valueTrendLabel: string
    navigateToAssetTooltip: string
    navigateToAssetAriaLabel: string
    assetImageAlt: string
}

const DEFAULT_GEOGRAPHIC_PORTFOLIO_POPUP_COPY: GeographicPortfolioPopupCopy = {
    marketValueLabel: 'Marktwert',
    valuePerSquareMeterLabel: 'Wert pro m²',
    valueTrendLabel: 'Wertentwicklung',
    navigateToAssetTooltip: 'Zur Immobilie',
    navigateToAssetAriaLabel: 'Immobilie öffnen',
    assetImageAlt: 'Asset Image',
}

export default function GeographicPortfolioPopup({
    date,
    title,
    market_value,
    value_per_sqm,
    navigateToAsset,
    image,
    imageThumbnail,
    locale = 'de-DE',
    copy,
}: GeographicPortfolioPopupProps) {
    const resolvedCopy = { ...DEFAULT_GEOGRAPHIC_PORTFOLIO_POPUP_COPY, ...copy }
    const formattedDate = date ? ` (${getLocalizedDateString(dayjs(date), false, locale)})` : ''
    const hasDetails = Boolean(image || imageThumbnail || market_value || value_per_sqm)

    return (
        <CardContainer>
            <PopupHeader $hasDetails={hasDetails}>
                <TextWithEllipsis tooltipText={title} typographyProps={{ variant: 'headline2', color: palette.customtext.darkblue100 }}>
                    {title}
                </TextWithEllipsis>
                {navigateToAsset && (
                    <IconButton
                        size="small"
                        variant="tertiary"
                        icon={<ArrowRightIcon />}
                        onClick={navigateToAsset}
                        tooltip={resolvedCopy.navigateToAssetTooltip}
                        ariaLabel={resolvedCopy.navigateToAssetAriaLabel}
                    />
                )}
            </PopupHeader>
            {hasDetails && (
                <PopupBody>
                    {(image || imageThumbnail) && (
                        <RoundImage
                            src={image}
                            alt={resolvedCopy.assetImageAlt}
                            height="102px"
                            width="150px"
                            fallbackImageSrc={[imageThumbnail, AssetPlaceholderImage].filter(isNotNullOrUndefined)}
                        />
                    )}
                    <ValuesContainer>
                        {market_value && (
                            <NumericLabeledValue
                                value={MonetaryValue.fromMonetaryValueDataObject(market_value.value)}
                                type="monetary"
                                unit="€"
                                label={`${resolvedCopy.marketValueLabel}${formattedDate}`}
                                variant="bodyTextMedium"
                                locale={locale}
                            />
                        )}
                        {(value_per_sqm || market_value?.trend !== undefined) && (
                            <ValueDetails market_value={market_value} value_per_sqm={value_per_sqm} locale={locale} copy={resolvedCopy} />
                        )}
                    </ValuesContainer>
                </PopupBody>
            )}
        </CardContainer>
    )
}

function ValueDetails({
    market_value,
    value_per_sqm,
    locale = 'de-DE',
    copy = DEFAULT_GEOGRAPHIC_PORTFOLIO_POPUP_COPY,
}: Pick<GeographicPortfolioPopupProps, 'market_value' | 'value_per_sqm' | 'locale'> & { copy?: GeographicPortfolioPopupCopy }) {
    return (
        <div style={{ display: 'flex', gap: tokens.SizingSpacing300 }}>
            {value_per_sqm && (
                <NumericLabeledValue
                    value={MonetaryValue.fromMonetaryValueDataObject(value_per_sqm)}
                    type="monetary"
                    unit="€"
                    label={copy.valuePerSquareMeterLabel}
                    variant="bodyTextMedium"
                    locale={locale}
                />
            )}
            {market_value?.trend !== undefined && (
                <HighlightedLabeledValue
                    value={new Percentage(market_value.trend).format({ withSign: true, locale })}
                    label={copy.valueTrendLabel}
                    variant="bodyTextMedium"
                    color={getTrendColor(market_value.trend)}
                />
            )}
        </div>
    )
}

const CardContainer = styled(Card, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(() => ({
    padding: `0 0 ${tokens.SizingSpacing100} ${tokens.SizingSpacing100}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.SizingSpacing050,
    width: '420px',
    borderRadius: tokens.SizingCornerM,
}))

const PopupHeader = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $hasDetails: boolean }>(({ $hasDetails }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.SizingSpacing200,
    padding: $hasDetails ? `0 ${tokens.SizingSpacing100} 0 0` : 0,
}))

const PopupBody = styled('div')(() => ({
    display: 'flex',
    gap: tokens.SizingSpacing300,
}))

const ValuesContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.SizingSpacing300,
}))
